/**
 * =============================================================================
 * COOKIE PROVIDER - Provider do Consentimento de Cookies
 * =============================================================================
 *
 * Carrega o consentimento do localStorage na montagem e o expõe à aplicação.
 * Atualiza o localStorage e o Google Consent Mode quando o consentimento muda.
 */

import {
  useCallback,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  type CookieCategories,
  type ConsentRecord,
  type SupportedLanguage,
  ALL_GRANTED,
  ALL_DENIED_EXCEPT_NECESSARY,
  DEFAULT_CATEGORIES,
  loadConsent,
  saveConsent,
  isConsentExpired,
  isConsentVersionOutdated,
  pushConsentToGTM,
  setDefaultConsent,
  loadGTMScript,
  loadGoogleAnalytics,
  detectLanguage,
  hasConsent as hasConsentHelper,
} from "@/utils/cookieManager";
import { CookieContext, type CookieContextValue, type CookieTranslations } from "@/context/CookieContext";

const translations: Record<SupportedLanguage, CookieTranslations> = {
  pt: {
    bannerTitle: "Utilizamos cookies",
    bannerDescription:
      "Este site utiliza cookies para melhorar a sua experiência. Pode aceitar todos, rejeitar os não essenciais ou personalizar as suas preferências.",
    acceptAll: "Aceitar todos",
    rejectNonEssential: "Rejeitar não essenciais",
    customize: "Personalizar",
    preferencesTitle: "Definições de cookies",
    preferencesDescription:
      "Escolha quais as categorias de cookies que autoriza. Os cookies necessários são essenciais para o funcionamento do site.",
    savePreferences: "Guardar preferências",
    necessary: "Necessários",
    analytics: "Analíticos",
    marketing: "Marketing",
    preferences: "Preferências",
    alwaysActive: "Sempre ativo",
    settingsLabel: "Definições de cookies",
    close: "Fechar",
    cookiePolicy: "Política de cookies",
    privacyPolicy: "Política de privacidade",
  },
  en: {
    bannerTitle: "We use cookies",
    bannerDescription:
      "This website uses cookies to improve your experience. You can accept all, reject non-essential, or customize your preferences.",
    acceptAll: "Accept all",
    rejectNonEssential: "Reject non-essential",
    customize: "Customize",
    preferencesTitle: "Cookie settings",
    preferencesDescription:
      "Choose which cookie categories you allow. Necessary cookies are essential for the website to function.",
    savePreferences: "Save preferences",
    necessary: "Necessary",
    analytics: "Analytics",
    marketing: "Marketing",
    preferences: "Preferences",
    alwaysActive: "Always active",
    settingsLabel: "Cookie settings",
    close: "Close",
    cookiePolicy: "Cookie policy",
    privacyPolicy: "Privacy policy",
  },
};

interface CookieProviderProps {
  children: ReactNode;
  gtmId?: string;
  gaId?: string;
  language?: SupportedLanguage;
  onConsentChange?: (record: ConsentRecord) => void;
}

function getInitialConsent(): {
  consent: CookieCategories | null;
  record: ConsentRecord | null;
  isBannerOpen: boolean;
} {
  // Definir default negado o mais cedo possível para GTM/GA
  setDefaultConsent(DEFAULT_CATEGORIES);

  const saved = loadConsent();

  if (!saved) {
    return { consent: null, record: null, isBannerOpen: true };
  }

  if (isConsentExpired(saved) || isConsentVersionOutdated(saved)) {
    return { consent: saved.categories, record: saved, isBannerOpen: true };
  }

  // Aplicar consentimento guardado
  pushConsentToGTM(saved.categories);
  if (saved.categories.analytics) {
    // GA e GTM serão carregados pelo provider com os IDs fornecidos
  }

  return { consent: saved.categories, record: saved, isBannerOpen: false };
}

export function CookieProvider({
  children,
  gtmId,
  gaId,
  language: forcedLanguage,
  onConsentChange,
}: CookieProviderProps) {
  const initial = useMemo(() => getInitialConsent(), []);

  const [consent, setConsent] = useState<CookieCategories | null>(initial.consent);
  const [record, setRecord] = useState<ConsentRecord | null>(initial.record);
  const [isBannerOpen, setIsBannerOpen] = useState(initial.isBannerOpen);
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);
  const [isLoading] = useState(false);
  const [language] = useState<SupportedLanguage>(forcedLanguage ?? detectLanguage());

  const t = useMemo(() => translations[language], [language]);

  const applyConsent = useCallback(
    (categories: CookieCategories) => {
      const savedRecord = saveConsent(categories);
      setConsent(categories);
      setRecord(savedRecord);
      pushConsentToGTM(categories);

      if (categories.analytics && gaId) {
        loadGoogleAnalytics(gaId);
      }

      if ((categories.analytics || categories.marketing) && gtmId) {
        loadGTMScript(gtmId);
      }

      onConsentChange?.(savedRecord);
    },
    [gaId, gtmId, onConsentChange]
  );

  const acceptAll = useCallback(() => {
    applyConsent(ALL_GRANTED);
    setIsBannerOpen(false);
    setIsPreferencesOpen(false);
  }, [applyConsent]);

  const rejectNonEssential = useCallback(() => {
    applyConsent(ALL_DENIED_EXCEPT_NECESSARY);
    setIsBannerOpen(false);
    setIsPreferencesOpen(false);
  }, [applyConsent]);

  const savePreferences = useCallback(
    (categories: CookieCategories) => {
      applyConsent(categories);
      setIsBannerOpen(false);
      setIsPreferencesOpen(false);
    },
    [applyConsent]
  );

  const openBanner = useCallback(() => setIsBannerOpen(true), []);
  const closeBanner = useCallback(() => setIsBannerOpen(false), []);
  const openPreferences = useCallback(() => setIsPreferencesOpen(true), []);
  const closePreferences = useCallback(() => setIsPreferencesOpen(false), []);

  const hasConsent = useCallback(
    (category: keyof CookieCategories) => {
      return hasConsentHelper(record, category);
    },
    [record]
  );

  const value = useMemo<CookieContextValue>(
    () => ({
      consent,
      record,
      isBannerOpen,
      isPreferencesOpen,
      isLoading,
      language,
      t,
      acceptAll,
      rejectNonEssential,
      savePreferences,
      openBanner,
      closeBanner,
      openPreferences,
      closePreferences,
      hasConsent,
    }),
    [
      consent,
      record,
      isBannerOpen,
      isPreferencesOpen,
      isLoading,
      language,
      t,
      acceptAll,
      rejectNonEssential,
      savePreferences,
      openBanner,
      closeBanner,
      openPreferences,
      closePreferences,
      hasConsent,
    ]
  );

  return <CookieContext.Provider value={value}>{children}</CookieContext.Provider>;
}

export default CookieProvider;
